import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Checkbox,
  Button,
  TextField,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Chip,
  Stack,
  Modal,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Badge,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";
import mammoth from "mammoth";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CommentIcon from "@mui/icons-material/Comment";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import axios from "axios";
import { apipost, apiget, apiput } from "../../service/api";
import { constant } from "../../constant";
import { formatRelativeTime } from "../../utils/formatTime";

export default function RequestDocuments({ loanId, borrowerEmail, loanNumber }) {
  const [incomeDocs, setIncomeDocs] = useState([]);
  const [assetDocs, setAssetDocs] = useState([]);
  const [newIncome, setNewIncome] = useState("");
  const [newAsset, setNewAsset] = useState("");
  const [loading, setLoading] = useState(false);
  const [viewDocUrl, setViewDocUrl] = useState(null);
  const [viewDocCanPreview, setViewDocCanPreview] = useState(true);
  const [openViewer, setOpenViewer] = useState(false);
  const [viewerTitle, setViewerTitle] = useState("Document Preview");
  const [viewDocHtml, setViewDocHtml] = useState(""); // For mammoth .docx rendering
  const [viewLoading, setViewLoading] = useState(null); // tracks "docId-attachmentIndex" being loaded

  // eFolder bucket state for move-on-approve
  const [eFolderBuckets, setEFolderBuckets] = useState([]);
  const [selectedBucket, setSelectedBucket] = useState("");

  // Comment editing state
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [commentDraft, setCommentDraft] = useState("");

  // Request modal state
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");
  const [docsToRequest, setDocsToRequest] = useState([]);

  // Rejection modal state
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectMessage, setRejectMessage] = useState("");
  const [docsToReject, setDocsToReject] = useState([]);

  // Snackbar notification state
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });
  const showNotification = (message, severity = "info") => {
    setSnackbar({ open: true, message, severity });
  };
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  useEffect(() => {
    fetchDocuments();
    fetchEFolderBuckets();

    // Poll for updates every 30s, but only when tab is visible
    const POLL_INTERVAL = 30000;
    let intervalId = null;

    const startPolling = () => {
      if (!intervalId) {
        intervalId = setInterval(fetchDocuments, POLL_INTERVAL);
      }
    };
    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibility = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchDocuments();
        startPolling();
      }
    };

    startPolling();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loanId]);

  // --- Fetch all documents for this loan ---
  const fetchDocuments = async () => {
    try {
      const res = await apiget(`loan/request-documents/${loanId}`);
      const docs = res?.data?.result || [];

      if (docs.length > 0) {
        const mapDoc = (d) => ({
          id: d._id,
          name: d.documentName,
          status: d.status,
          checked: false,
          comment: d.comment || "",
          rejectionReason: d.rejectionReason || "",
          modifiedOn: d.modifiedOn,
          hasFile: !!(d.attachments?.length > 0 || d.encompassAttachmentId),
          fileCount: d.attachments?.length || (d.encompassAttachmentId ? 1 : 0),
          attachments: d.attachments || [],
        });

        setIncomeDocs(docs.filter((d) => d.category === "Income").map(mapDoc));
        setAssetDocs(docs.filter((d) => d.category === "Asset").map(mapDoc));
      }
    } catch (err) {
      console.error("Error fetching existing docs:", err);
    }
  };

  // --- Fetch eFolder buckets from Encompass for the bucket selector ---
  const fetchEFolderBuckets = async () => {
    try {
      const res = await apiget(`loan/efolder/${loanId}`);
      const docs = res?.data?.result || [];
      setEFolderBuckets(docs.map((d) => ({ id: d.documentId || d.id, title: d.title })));
    } catch (err) {
      console.error("Error fetching eFolder buckets:", err);
    }
  };

  // --- Status color helper ---
  const getStatusColor = (status) => {
    switch (status) {
      case "Added":
        return "default";
      case "Requested":
        return "info";
      case "Received":
        return "success";
      case "Uploaded to Encompass":
        return "secondary";
      case "Rejected":
        return "error";
      default:
        return "warning";
    }
  };

  // --- Checkbox toggle ---
  const handleCheck = (type, id) => {
    const setter = type === "income" ? setIncomeDocs : setAssetDocs;
    setter((prev) =>
      prev.map((doc) => (doc.id === id ? { ...doc, checked: !doc.checked } : doc))
    );
  };

  // --- Add custom document (persists to DB) ---
  const handleAdd = async (type) => {
    const docName = type === "income" ? newIncome.trim() : newAsset.trim();
    if (!docName) return;

    try {
      const payload = {
        loanId,
        borrowerEmail,
        documents: [
          {
            name: docName,
            category: type === "income" ? "Income" : "Asset",
            status: "Added",
          },
        ],
      };

      await apipost(`loan/request-documents/${loanId}`, payload);

      if (type === "income") setNewIncome("");
      else setNewAsset("");

      // Refresh from DB to get the real _id
      await fetchDocuments();
    } catch (err) {
      console.error("Error adding document:", err);
      showNotification("Failed to add document.", "error");
    }
  };

  // --- Save comment for a document ---
  const handleSaveComment = async (docId) => {
    try {
      await apiput(`loan/request-documents/comment/${docId}`, {
        comment: commentDraft,
      });
      // Update local state
      const updateDocs = (prev) =>
        prev.map((d) => (d.id === docId ? { ...d, comment: commentDraft } : d));
      setIncomeDocs(updateDocs);
      setAssetDocs(updateDocs);
      setEditingCommentId(null);
      setCommentDraft("");
    } catch (err) {
      console.error("Error saving comment:", err);
      showNotification("Failed to save comment.", "error");
    }
  };

  // --- Open request modal for selected documents ---
  const handleOpenRequestModal = () => {
    const selected = [
      ...incomeDocs.filter((d) => d.checked),
      ...assetDocs.filter((d) => d.checked),
    ];
    if (selected.length === 0) {
      showNotification("Select at least one document to request.", "warning");
      return;
    }
    setDocsToRequest(selected);
    setRequestMessage("");
    setRequestModalOpen(true);
  };

  // --- Submit document request with optional message ---
  const handleRequestDocuments = async () => {
    setLoading(true);
    try {
      const payload = {
        loanId,
        borrowerEmail,
        loanNumber,
        additionalMessage: requestMessage.trim() || undefined,
        documents: docsToRequest.map((d) => ({
          name: d.name,
          category: incomeDocs.find((i) => i.id === d.id) ? "Income" : "Asset",
          status: "Requested",
          comment: d.comment || "",
        })),
      };

      await apipost(`loan/request-documents/${loanId}`, payload);

      showNotification("Documents requested and borrower notified!", "success");
      setRequestModalOpen(false);
      setRequestMessage("");
      setDocsToRequest([]);
      fetchDocuments();
    } catch (err) {
      console.error("Error requesting docs:", err);
      showNotification("Failed to request documents.", "error");
    } finally {
      setLoading(false);
    }
  };

  // --- Approve & send selected received docs to Encompass (with optional bucket move) ---
  const handleSendToEncompass = async () => {
    const received = [
      ...incomeDocs.filter((d) => d.checked && d.status === "Received"),
      ...assetDocs.filter((d) => d.checked && d.status === "Received"),
    ];
    if (received.length === 0) {
      showNotification("Select at least one received document to approve.", "warning");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        loanId,
        documents: received.map((d) => d.name),
        targetDocumentId: selectedBucket || undefined,
      };
      await apipost(`loan/send-to-encompass/${loanId}`, payload);

      showNotification("Documents approved successfully!", "success");
      fetchDocuments();
    } catch (err) {
      console.error("Error sending docs to Encompass:", err);
      showNotification("Failed to approve documents.", "error");
    } finally {
      setLoading(false);
    }
  };

  // --- Open rejection modal for selected received docs ---
  const handleOpenRejectModal = () => {
    const received = [
      ...incomeDocs.filter((d) => d.checked && d.status === "Received"),
      ...assetDocs.filter((d) => d.checked && d.status === "Received"),
    ];
    if (received.length === 0) {
      showNotification("Select at least one received document to reject.", "warning");
      return;
    }
    setDocsToReject(received);
    setRejectionReason("");
    setRejectMessage("");
    setRejectModalOpen(true);
  };

  // --- Submit rejection ---
  const handleRejectDocuments = async () => {
    if (!rejectionReason.trim()) {
      showNotification("Please provide a reason for rejection.", "warning");
      return;
    }

    setLoading(true);
    try {
      const payload = {
        documents: docsToReject.map((d) => ({
          name: d.name,
          rejectionReason: rejectionReason.trim(),
        })),
        borrowerEmail,
        loanNumber,
        additionalMessage: rejectMessage.trim() || undefined,
      };
      await apipost(`loan/reject-documents/${loanId}`, payload);

      showNotification("Documents rejected and borrower notified.", "success");
      setRejectModalOpen(false);
      setRejectionReason("");
      setDocsToReject([]);
      fetchDocuments();
    } catch (err) {
      console.error("Error rejecting docs:", err);
      showNotification("Failed to reject documents.", "error");
    } finally {
      setLoading(false);
    }
  };

  // Types that browsers can render inline in an iframe
  const canPreviewInline = (blobType) => {
    if (!blobType) return false;
    return (
      blobType.startsWith("application/pdf") ||
      blobType.startsWith("image/") ||
      blobType.startsWith("text/")
    );
  };

  // Detect Word document types (.docx) for mammoth conversion
  const isWordDocument = (blobType, fileName) => {
    if (
      blobType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      blobType === "application/msword"
    ) {
      return true;
    }
    const ext = (fileName || "").split(".").pop().toLowerCase();
    return ext === "docx" || ext === "doc";
  };

  // --- View document via proxy (fetch as blob for auth) ---
  const handleViewDocument = async (doc, attachmentIndex = 0) => {
    if (!doc.hasFile) {
      showNotification("No document file available to preview.", "info");
      return;
    }

    const loadingKey = `${doc.id}-${attachmentIndex}`;
    setViewLoading(loadingKey);

    try {
      const token = localStorage.getItem("token") || "";
      const url = `${constant.baseUrl}loan/view-attachment/${loanId}/${doc.id}?attachmentIndex=${attachmentIndex}`;
      const response = await axios.get(url, {
        responseType: "blob",
        headers: token ? { Authorization: token } : {},
      });

      const blob = response.data;

      // Set title with file name if available
      const fileName =
        doc.attachments?.[attachmentIndex]?.fileName || doc.name;
      setViewerTitle(`Preview: ${fileName}`);

      // Check if this is a Word document we can convert with mammoth
      if (isWordDocument(blob.type, fileName)) {
        try {
          const arrayBuffer = await blob.arrayBuffer();
          const result = await mammoth.convertToHtml({ arrayBuffer });
          setViewDocHtml(result.value);
          setViewDocUrl(null); // No blob URL needed for HTML rendering
          setViewDocCanPreview(true);
          setOpenViewer(true);
          return;
        } catch (convErr) {
          console.error("Mammoth conversion failed:", convErr);
          // Fall through to normal blob handling
        }
      }

      const blobUrl = URL.createObjectURL(blob);
      setViewDocUrl(blobUrl);
      setViewDocHtml(""); // Clear any previous HTML
      setViewDocCanPreview(canPreviewInline(blob.type));
      setOpenViewer(true);
    } catch (err) {
      console.error("Error loading document preview:", err);
      showNotification("Failed to load document preview.", "error");
    } finally {
      setViewLoading(null);
    }
  };

  // --- Close modal ---
  const handleCloseViewer = () => {
    if (viewDocUrl) URL.revokeObjectURL(viewDocUrl);
    setOpenViewer(false);
    setViewDocUrl(null);
    setViewDocHtml("");
    setViewDocCanPreview(true);
    setViewerTitle("Document Preview");
  };

  // --- Render section (Income or Assets) ---
  const renderSection = (title, docs, type, newValue, setValue) => (
    <Accordion defaultExpanded sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Typography variant="h6">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        {docs.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No {title.toLowerCase()} documents added yet.
          </Typography>
        )}
        {docs.map((doc) => (
          <Box
            key={doc.id}
            sx={{
              mb: 2,
              pb: 1.5,
              borderBottom: "1px solid #eee",
              "&:last-of-type": { borderBottom: "none" },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={doc.checked}
                    onChange={() => handleCheck(type, doc.id)}
                  />
                }
                label={doc.name}
              />

              <Stack direction="row" alignItems="center" spacing={1}>
                {/* File count badge */}
                {doc.fileCount > 0 && (
                  <Tooltip title={`${doc.fileCount} file(s) uploaded`}>
                    <Badge badgeContent={doc.fileCount} color="primary" max={99}>
                      <AttachFileIcon fontSize="small" color="action" />
                    </Badge>
                  </Tooltip>
                )}

                <Chip
                  label={doc.status}
                  color={getStatusColor(doc.status)}
                  size="small"
                  sx={{ fontWeight: 500 }}
                />

                {doc.modifiedOn && (
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                    {formatRelativeTime(doc.modifiedOn)}
                  </Typography>
                )}

                {/* Comment toggle button */}
                <Tooltip title={doc.comment ? "Edit comment" : "Add comment"}>
                  <IconButton
                    size="small"
                    color={doc.comment ? "primary" : "default"}
                    onClick={() => {
                      if (editingCommentId === doc.id) {
                        setEditingCommentId(null);
                        setCommentDraft("");
                      } else {
                        setEditingCommentId(doc.id);
                        setCommentDraft(doc.comment || "");
                      }
                    }}
                  >
                    <CommentIcon fontSize="small" />
                  </IconButton>
                </Tooltip>

                {/* View buttons - one per attachment or single if legacy */}
                {doc.attachments && doc.attachments.length > 1 ? (
                  doc.attachments.map((att, idx) => (
                    <Button
                      key={idx}
                      variant="outlined"
                      size="small"
                      color="primary"
                      disabled={viewLoading === `${doc.id}-${idx}`}
                      onClick={() => handleViewDocument(doc, idx)}
                    >
                      {viewLoading === `${doc.id}-${idx}` ? (
                        <CircularProgress size={16} />
                      ) : (
                        `View ${idx + 1}`
                      )}
                    </Button>
                  ))
                ) : (
                  <Button
                    variant="outlined"
                    size="small"
                    color="primary"
                    disabled={!doc.hasFile || viewLoading === `${doc.id}-0`}
                    onClick={() => handleViewDocument(doc, 0)}
                  >
                    {viewLoading === `${doc.id}-0` ? (
                      <CircularProgress size={16} />
                    ) : (
                      "View"
                    )}
                  </Button>
                )}
              </Stack>
            </Stack>

            {/* Show file names when files exist */}
            {doc.attachments && doc.attachments.length > 0 && (
              <Typography
                variant="caption"
                sx={{ ml: 4, mt: 0.5, color: "text.secondary", display: "block" }}
              >
                {doc.attachments.map((att) => att.fileName).join(" \u2022 ")}
              </Typography>
            )}

            {/* Show existing comment (read-only) */}
            {doc.comment && editingCommentId !== doc.id && (
              <Typography
                variant="body2"
                sx={{ ml: 4, mt: 0.5, color: "text.secondary", fontStyle: "italic" }}
              >
                Note: {doc.comment}
              </Typography>
            )}

            {/* Show rejection reason */}
            {doc.rejectionReason && doc.status === "Rejected" && (
              <Typography
                variant="body2"
                sx={{ ml: 4, mt: 0.5, color: "error.main", fontStyle: "italic" }}
              >
                Rejection Reason: {doc.rejectionReason}
              </Typography>
            )}

            {/* Comment editor */}
            {editingCommentId === doc.id && (
              <Box sx={{ ml: 4, mt: 1, display: "flex", gap: 1, alignItems: "flex-start" }}>
                <TextField
                  size="small"
                  multiline
                  minRows={1}
                  maxRows={3}
                  fullWidth
                  placeholder="Add a comment for the borrower..."
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                />
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleSaveComment(doc.id)}
                >
                  Save
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setEditingCommentId(null);
                    setCommentDraft("");
                  }}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>
        ))}

        <Box sx={{ display: "flex", mt: 1 }}>
          <TextField
            size="small"
            value={newValue}
            label={`Add ${title} Doc`}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                handleAdd(type);
              }
            }}
          />
          <Button sx={{ ml: 1 }} variant="contained" onClick={() => handleAdd(type)}>
            Add
          </Button>
        </Box>
      </AccordionDetails>
    </Accordion>
  );

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" mb={2}>
        Request Documents
      </Typography>

      {incomeDocs.length === 0 && assetDocs.length === 0 && (
        <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
          <Typography variant="body1" color="text.secondary">
            No document requests yet. Use the fields below to add income or asset
            documents, then click &quot;Request Documents&quot; to send them to the borrower.
          </Typography>
        </Box>
      )}

      {renderSection("Income", incomeDocs, "income", newIncome, setNewIncome)}
      {renderSection("Assets", assetDocs, "asset", newAsset, setNewAsset)}

      <Box sx={{ mt: 4, display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel>Move to Bucket</InputLabel>
          <Select
            value={selectedBucket}
            onChange={(e) => setSelectedBucket(e.target.value)}
            label="Move to Bucket"
          >
            <MenuItem value="">
              <em>Leave in Borrower Uploads</em>
            </MenuItem>
            {eFolderBuckets.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="contained"
          color="error"
          onClick={handleOpenRejectModal}
          disabled={loading}
        >
          Reject &amp; Re-request
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={handleSendToEncompass}
          disabled={loading}
        >
          {loading ? "Processing..." : "Approve & Send to Encompass"}
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenRequestModal}
          disabled={loading}
        >
          {loading ? "Requesting..." : "Request Documents"}
        </Button>
      </Box>

      {/* Modal for viewing document */}
      <Modal
        open={openViewer}
        onClose={handleCloseViewer}
        aria-labelledby="view-document-modal"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: "90%",
            height: "90%",
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: "1px solid #ddd",
              bgcolor: "#f8f8f8",
            }}
          >
            <Typography variant="h6">{viewerTitle}</Typography>
            <Button onClick={handleCloseViewer} variant="outlined" color="error">
              Close
            </Button>
          </Box>

          {/* Mammoth-rendered Word document */}
          {viewDocHtml ? (
            <Box
              sx={{
                p: 3,
                height: "calc(100% - 64px)",
                overflow: "auto",
                "& img": { maxWidth: "100%" },
                "& table": {
                  borderCollapse: "collapse",
                  width: "100%",
                  "& td, & th": {
                    border: "1px solid #ddd",
                    padding: "8px",
                  },
                },
              }}
              dangerouslySetInnerHTML={{ __html: viewDocHtml }}
            />
          ) : viewDocUrl && viewDocCanPreview ? (
            <iframe
              src={viewDocUrl}
              title="Document Viewer"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          ) : viewDocUrl && !viewDocCanPreview ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: "80%",
                gap: 2,
              }}
            >
              <Typography variant="body1" color="text.secondary">
                This file type cannot be previewed in the browser.
              </Typography>
              <Button
                variant="contained"
                href={viewDocUrl}
                download={viewerTitle.replace("Preview: ", "")}
              >
                Download File
              </Button>
            </Box>
          ) : (
            <Typography
              sx={{ p: 4, textAlign: "center" }}
              variant="body1"
              color="text.secondary"
            >
              No document to display
            </Typography>
          )}
        </Box>
      </Modal>

      {/* Request Documents Modal */}
      <Modal open={requestModalOpen} onClose={() => setRequestModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 550,
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" mb={2}>
            Request Documents
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            The following documents will be requested from the borrower:
          </Typography>
          <Box sx={{ mb: 2 }}>
            {docsToRequest.map((d) => (
              <Box key={d.id} sx={{ mb: 1 }}>
                <Chip label={d.name} size="small" sx={{ mr: 0.5 }} />
                {d.comment && (
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                    Note: {d.comment}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label="Additional Message to Borrower (optional)"
            placeholder="Add any additional instructions or context for the borrower..."
            value={requestMessage}
            onChange={(e) => setRequestMessage(e.target.value)}
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
            <Button variant="outlined" onClick={() => setRequestModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleRequestDocuments}
              disabled={loading}
            >
              {loading ? "Sending..." : "Send Request"}
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Rejection Reason Modal */}
      <Modal open={rejectModalOpen} onClose={() => setRejectModalOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 550,
            bgcolor: "background.paper",
            boxShadow: 24,
            borderRadius: 2,
            p: 4,
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <Typography variant="h6" mb={2}>
            Reject Documents
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={2}>
            The following documents will be rejected and the borrower will be asked to re-upload:
          </Typography>
          <Box sx={{ mb: 2 }}>
            {docsToReject.map((d) => (
              <Chip key={d.id} label={d.name} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
            ))}
          </Box>
          <TextField
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            label="Rejection Reason"
            placeholder="e.g., Image is blurry, wrong document uploaded, missing pages..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            label="Additional Message to Borrower (optional)"
            placeholder="Add any additional context or instructions..."
            value={rejectMessage}
            onChange={(e) => setRejectMessage(e.target.value)}
          />
          <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
            <Button variant="outlined" onClick={() => setRejectModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleRejectDocuments}
              disabled={loading}
            >
              {loading ? "Rejecting..." : "Confirm Reject"}
            </Button>
          </Stack>
        </Box>
      </Modal>

      {/* Notification Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%", fontSize: "0.95rem" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
